import { useEffect, useMemo, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { useSelector } from "react-redux";
import {
  adminModeNavSections,
  instructorNavSections,
  managerNavSections,
} from "./MainSidebar";

function safeReadModeFlags() {
  if (typeof window === "undefined") return { adminMode: false, managerMode: false };
  try {
    return {
      adminMode: window.localStorage.getItem("zindua_admin_mode") === "on",
      managerMode: window.localStorage.getItem("zindua_manager_mode") === "on",
    };
  } catch {
    return { adminMode: false, managerMode: false };
  }
}

function flattenNavSections(sections) {
  const out = [];

  const visit = (node) => {
    if (!node) return;

    // Leaf link
    if (typeof node?.title === "string" && typeof node?.path === "string") {
      out.push({ title: node.title, path: node.path });
      return;
    }

    // Section or group
    const children = Array.isArray(node?.items) ? node.items : [];
    children.forEach(visit);
  };

  (Array.isArray(sections) ? sections : []).forEach(visit);
  return out;
}

function pickTitleFromEntries(entries, pathname, search) {
  const safePathname = pathname || "/";
  const safeSearch = search || "";
  const fullPath = `${safePathname}${safeSearch}`;

  // Prefer exact full-path matches (supports sidebar links with query strings).
  const exactFull = entries.find((e) => e?.path === fullPath);
  if (exactFull?.title) return exactFull.title;

  // Fall back to pathname-only matches.
  const exactPathname = entries.find((e) => typeof e?.path === "string" && e.path.split("?")[0] === safePathname);
  if (exactPathname?.title) return exactPathname.title;

  return null;
}

export function useSidebarPageTitle() {
  const location = useLocation();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");

  const [modeFlags, setModeFlags] = useState(() => safeReadModeFlags());

  useEffect(() => {
    const handle = () => setModeFlags(safeReadModeFlags());
    if (typeof window === "undefined") return undefined;
    window.addEventListener("zindua-mode-changed", handle);
    window.addEventListener("storage", handle);
    return () => {
      window.removeEventListener("zindua-mode-changed", handle);
      window.removeEventListener("storage", handle);
    };
  }, []);

  const navMode = useMemo(() => {
    if (isAdmin && modeFlags.adminMode) return "admin";
    if (!isAdmin && isManager && modeFlags.managerMode) return "manager";
    return "instructor";
  }, [isAdmin, isManager, modeFlags.adminMode, modeFlags.managerMode]);

  const entries = useMemo(() => {
    if (navMode === "admin") return flattenNavSections(adminModeNavSections);
    if (navMode === "manager") return flattenNavSections(managerNavSections);
    return flattenNavSections(instructorNavSections);
  }, [navMode]);

  return useMemo(() => {
    const pathname = location?.pathname || "/";
    const search = location?.search || "";
    return pickTitleFromEntries(entries, pathname, search);
  }, [entries, location?.pathname, location?.search]);
}
